function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import produce from 'immer';
import { CountBy } from 'common/query-builder/query-entry-section/types';
import { MetricCountBySelection } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/metric-count-by-menu';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { Section } from 'common/components/items-menu/types';
import { Directions } from 'common/components/drop-menu/types';
import { ButtonSize } from 'common/components/button/constants';
import { useTranslation } from 'common/i18n/hooks';
import { getStepSelectionLabel, getStepSelectionOptions } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/funnels/funnel-step-selection';
import { ChangeEvent } from 'common/components/select/types';
import { RangeSelection } from 'common/widgets/step-range-selector/types';
import { ConversionWindowUnit, FUNNELS_MATH_TYPES, MetricType } from 'common/types/reports/bookmark';
import { getCountUsersOnceTypeFromEntrySelection } from 'common/query-builder/query-block/count-menu/util';
import { getFunnelMetricAttributionItems } from 'common/report/insights/util/funnel-attribution';
import { MultiAttributionMeasurement } from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/common/multi-attribution';
import type { RowProps } from '../catalog';
import styles from './index.module.styl';
export function FunnelMetricMeasurement(props: RowProps) {
  var _getFunnelMetricAttri;
  const {
    print,
    t
  } = useTranslation();
  const breakdownAttributionLabel = props.sectionProps.breakdownAttributionLabeler ? print(props.sectionProps.breakdownAttributionLabeler(print((_getFunnelMetricAttri = getFunnelMetricAttributionItems(props.entry).find(entry => entry.isSelected)) === null || _getFunnelMetricAttri === void 0 ? void 0 : _getFunnelMetricAttri.label) || "...")) : "...";
  const handleStepSelection = (ev: ChangeEvent<RangeSelection>) => {
    var _value$range;
    const {
      value
    } = ev.detail.item;
    const entry = _objectSpread(_objectSpread({}, props.entry), {}, {
      measurement: _objectSpread(_objectSpread({}, props.entry.measurement), {}, {
        stepIndex: value !== null && value !== void 0 && value.isEntireRange ? null : value === null || value === void 0 || (_value$range = value.range) === null || _value$range === void 0 ? void 0 : _value$range.from
      })
    });
    props.updateEntry(entry);
  };
  const stepSelectionSections: Section[] = [{
    icon: "info-xs",
    tooltip: {
      text: t("query-builder.step-selection.tooltip")
    },
    label: t("query-builder.step-selection.title"),
    items: getStepSelectionOptions({
      entry: props.entry,
      chartType: props.sectionProps.chartType!
    })
  }];
  const stepSelectionLabel = getStepSelectionLabel({
    entry: props.entry,
    chartType: props.sectionProps.chartType!
  });
  const handleFunnelsCountBySelected = (countBy: CountBy) => {
    const {
      aggregationOperator,
      property,
      countUsersOnceType
    } = countBy;
    const updatedEntry = produce(props.entry, entry => {
      if ([FUNNELS_MATH_TYPES.SESSION, FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION].includes(aggregationOperator as FUNNELS_MATH_TYPES)) {
        entry.behavior.conversionWindowDuration = 1;
        entry.behavior.conversionWindowUnit = ConversionWindowUnit.Session;
      }
      // if stepIndex is 0(Entering Funnel), set it to null(All Steps) if someone selects conversion rate measurement
      if ([FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL, FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE, FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION].includes(aggregationOperator as FUNNELS_MATH_TYPES) && entry.measurement.stepIndex === 0) {
        entry.measurement.stepIndex = null;
      }
      entry.measurement.aggregationOperator = aggregationOperator;
      entry.measurement.property = property;
      entry.measurement.countUsersOnceType = getCountUsersOnceTypeFromEntrySelection({
        selectedCountUsersOnceType: countUsersOnceType,
        metricType: MetricType.Funnel,
        multiAttribution: entry.measurement.multiAttribution
      });
      return entry;
    });
    props.updateEntry(updatedEntry);
  };
  const handleAttributionSelection = (value: string | number) => {
    const updatedEntry = produce(props.entry, entry => {
      entry.measurement.attributionSelection = value;
    });
    props.updateEntry(updatedEntry);
  };
  return <div className={styles.measurement} data-sentry-component="FunnelMetricMeasurement" data-sentry-source-file="measurement.tsx">
      <MetricCountBySelection {...props} onMetricCountBySelected={handleFunnelsCountBySelected} data-sentry-element="MetricCountBySelection" data-sentry-source-file="measurement.tsx" />

      {/* Step selection */}
      <SelectReact alwaysShowCaret label={stepSelectionLabel} theme={SelectTheme.Tertiary} size={ButtonSize.Small} menuAlignment={Directions.Right} menuWidth={184} disableFirstItemPadding elref='advancedCountMenuSelect' sections={stepSelectionSections} onChange={ev => handleStepSelection(ev as ChangeEvent<RangeSelection>)} data-sentry-element="SelectReact" data-sentry-source-file="measurement.tsx" />

      {/* Funnel event attribution selection */}
      {!props.sectionProps.canUseMultiAttribution && props.sectionProps.canUseBreakdownAttribution && <SelectReact elref='attributionSelect' alwaysShowCaret sections={[{
      items: getFunnelMetricAttributionItems(props.entry)
    }]} label={breakdownAttributionLabel} selectedOption='' size={ButtonSize.Small} theme={SelectTheme.Tertiary} menuWidth={200} tabIndex={0} experimentalPositioning onChange={ev => handleAttributionSelection("detail" in ev && ev.detail.item.value)} />}

      {/* Multi Attribution selection */}
      <MultiAttributionMeasurement {...props} extraLabel={breakdownAttributionLabel} data-sentry-element="MultiAttributionMeasurement" data-sentry-source-file="measurement.tsx" />
    </div>;
}